import React, { useState, useReducer, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setMessageHandler } from "../../../reducers/selectedValues";
import useHttpRequests from "../../../hooks/useHttpRequests";
import { Grid, CircularProgress, Backdrop } from "@mui/material";
import { InputDate } from "../../uiControls/inputDate";
import { MessageDisplay } from "../../common/messageDisplay";
import { ReportsSelectCheckboxs } from "../reports/reportsSelectCheckboxs";
import { TableForMissingDates } from "./tableForMissingDates";
import { AddNew } from "../../uiControls/addNew";
import { ConfirmationDialog } from "../../common/confirmationDialog";
import * as utils from "../../common/utils";

const missingReasonsDateColumns = [
  {
    label: "",
    keyField: "dateKey",
    dataField: "dateKey",
    width: "30px",
    fieldType: "number",
  },
  {
    label: "Missing Dates",
    keyField: "dateKey",
    dataField: "dateKey",
    fieldType: "date",
    width: "130px",
  },
  {
    label: "Reasons",
    keyField: "dateKey",
    dataField: "reason",
    fieldType: "label",
  },
];

const initialMissingDataReasonsDetails = {
  startDate: getStartDate(),
  endDate: new Date().toISOString(),
  ddlReasons: 0,
  otherReason: "",
  checkDelete: false,
  checkAll: false,
  isLoadButtonClicked: false,
};

function getStartDate() {
  const d = new Date();
  const newDate = new Date(d.getFullYear() - 1 + "-10-01");
  return newDate.toISOString();
}

export const MissingDataReasons = () => {
  const facilityId = useSelector((state) => state.SelectedValues.facilityId);
  const messageDispatch = useDispatch();
  const [selectedRows, setSelectedRows] = useState([]);
  const [missingReasons, setMissingReasons] = useState([]);
  const [missingReasonsDates, setMissingReasonsDates] = useState([]);
  const [missingDataReasonsDetails, setMissingDataReasonsDetails] = useReducer(
    (state, updates) => ({ ...state, ...updates }),
    initialMissingDataReasonsDetails
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmationDialogAttributes, setConfirmationDialogAttributes] =
    useState({});

  const {
    isLoading: isLoadingMissingReasons,
    sendRequest: fetchMissingReasons,
  } = useHttpRequests();
  const {
    isLoading: isLoadingMissingReasonsDates,
    sendRequest: fetchMissingReasonsDates,
  } = useHttpRequests();

  const { isLoading: isLoadingMissingDataSave, sendRequest: saveMissingData } =
    useHttpRequests();

  const {
    isLoading: isLoadingMissingDataDelete,
    sendRequest: deleteMissingData,
  } = useHttpRequests();

  const updateMissingDataReasonsDetailsValue = (key, value) => {
    if (typeof key === "object") {
      setMissingDataReasonsDetails({ ...missingDataReasonsDetails, ...key });
    } else {
      setMissingDataReasonsDetails({
        ...missingDataReasonsDetails,
        [key]: value,
      });
    }
    if (value) {
      messageDispatch(setMessageHandler({ type: null, message: null }));
    }
  };

  const onDateChangeHandler = (e, controlName) => {
    updateMissingDataReasonsDetailsValue(controlName, e);
  };

  const getMissingReasons = () => {
    const getMissingReasonsResponse = (obj) => {
      if (
        !obj ||
        !obj["isSuccess"] ||
        (obj["validationErrors"] !== undefined &&
          obj["validationErrors"]?.length > 0)
      ) {
        messageDispatch(
          setMessageHandler({
            type: "error",
            message:
              (obj["message"] ? obj["message"] : "") +
              (obj["validationErrors"]?.length > 0
                ? "\n" + obj["validationErrors"]?.join("\n")
                : ""),
          })
        );
        return;
      }
      setMissingReasons(
        obj["reasons"]?.map((t) => {
          return { id: t["reasonKey"], label: t["reason"] };
        })
      );
      updateMissingDataReasonsDetailsValue(initialMissingDataReasonsDetails);
    };

    if (facilityId > 0) {
      fetchMissingReasons(
        {
          url: "MissingReasons",
          method: "GET",
          header: true,
        },
        getMissingReasonsResponse
      );
    }
  };

  useEffect(() => {
    getMissingReasons();
  }, [facilityId]);

  const getMissingReasonsByDates = () => {
    const getMissingReasonsByDatesResponse = (obj) => {
      if (
        !obj ||
        !obj["isSuccess"] ||
        (obj["validationErrors"] !== undefined &&
          obj["validationErrors"]?.length > 0)
      ) {
        messageDispatch(
          setMessageHandler({
            type: "error",
            message:
              (obj["message"] ? obj["message"] : "") +
              (obj["validationErrors"]?.length > 0
                ? "\n" + obj["validationErrors"]?.join("\n")
                : ""),
          })
        );
        return;
      }
      setMissingReasonsDates(obj["reasons"]);
      updateMissingDataReasonsDetailsValue({
        isLoadButtonClicked: true,
        checkDelete: false,
      });
      setSelectedRows([]);
    };

    if (facilityId > 0) {
      fetchMissingReasonsDates(
        {
          url:
            "MissingReasons/" +
            facilityId +
            "/" +
            utils.formatDateISOWithHyphen(missingDataReasonsDetails.startDate) +
            "/" +
            utils.formatDateISOWithHyphen(missingDataReasonsDetails.endDate),
          method: "GET",
          header: true,
        },
        getMissingReasonsByDatesResponse
      );
    }
  };

  const indexChangeHandler = (field, id, value) => {
    if (value) {
      updateMissingDataReasonsDetailsValue(id, value);
    }
  };
  const onChangeHandler = (e, controlName) => {
    updateMissingDataReasonsDetailsValue(
      controlName,
      e.target.type === "checkbox" ? e.target.checked : e.target.value
    );
    if (controlName === "checkAll") {
      e.target.checked
        ? setSelectedRows(missingReasonsDates)
        : setSelectedRows([]);
    }
  };

  const checkOtherReasonDisabled = () => {
    const selectedReason = missingReasons?.filter(
      (t) => t.id === missingDataReasonsDetails.ddlReasons
    );
    if (selectedReason && selectedReason[0]?.label === "Other") {
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (
      missingReasonsDates?.length > 0 &&
      selectedRows?.length === missingReasonsDates?.length
    ) {
      updateMissingDataReasonsDetailsValue("checkAll", true);
    } else {
      updateMissingDataReasonsDetailsValue("checkAll", false);
    }
  }, [selectedRows]);

  const validateMissingData = () => {
    let errMsg = "";
    if (selectedRows?.length <= 0) {
      errMsg += (errMsg ? "\n" : "") + "Select dates to update...";
    }
    if (
      !missingDataReasonsDetails.checkDelete &&
      missingDataReasonsDetails.ddlReasons <= 0
    ) {
      errMsg += (errMsg ? "\n" : "") + "Select reason to update...";
    }
    if (errMsg) {
      messageDispatch(setMessageHandler({ type: "error", message: errMsg }));
      return;
    }

    if (missingDataReasonsDetails.checkDelete) {
      confirmMissingDatesDataDelete();
    } else {
      saveMissingDatesData();
    }
  };

  const saveMissingDatesData = () => {
    const data = {
      facilityKey: facilityId,
      dateKeys: selectedRows?.map((t) => {
        return t.dateKey;
      }),
      reasonKey: missingDataReasonsDetails.ddlReasons,
      comment: missingDataReasonsDetails.otherReason,
    };
    const saveMissingDatesDataResponse = (obj) => {
      if (
        !obj ||
        !obj["isSuccess"] ||
        (obj["validationErrors"] !== undefined &&
          obj["validationErrors"]?.length > 0)
      ) {
        messageDispatch(
          setMessageHandler({
            type: "error",
            message:
              (obj["message"] ? obj["message"] : "") +
              (obj["validationErrors"]?.length > 0
                ? "\n" + obj["validationErrors"]?.join("\n")
                : ""),
          })
        );
        return;
      }
      messageDispatch(
        setMessageHandler({
          type: "success",
          message: "Missing dates updated successfully!",
        })
      );
      setTimeout(() => {
        messageDispatch(setMessageHandler({ type: null, message: null }));
        getMissingReasonsByDates();
      }, 2000);
    };

    if (facilityId > 0) {
      saveMissingData(
        { url: "MissingReasons", body: data },
        saveMissingDatesDataResponse
      );
    }
  };

  const deleteMissingDatesData = () => {
    const data = {
      facilityKey: facilityId,
      dateKeys: selectedRows?.map((t) => {
        return t.dateKey;
      }),
    };
    const deleteMissingDatesDataResponse = (obj) => {
      if (
        !obj ||
        !obj["isSuccess"] ||
        (obj["validationErrors"] !== undefined &&
          obj["validationErrors"]?.length > 0)
      ) {
        messageDispatch(
          setMessageHandler({
            type: "error",
            message:
              (obj["message"] ? obj["message"] : "") +
              (obj["validationErrors"]?.length > 0
                ? "\n" + obj["validationErrors"]?.join("\n")
                : ""),
          })
        );
        return;
      }
      messageDispatch(
        setMessageHandler({
          type: "success",
          message: "Missing dates deleted successfully!",
        })
      );
      setTimeout(() => {
        messageDispatch(setMessageHandler({ type: null, message: null }));
        getMissingReasonsByDates();
      }, 2000);
    };

    if (facilityId > 0) {
      deleteMissingData(
        { url: "MissingReasons", method: "DELETE", body: data },
        deleteMissingDatesDataResponse
      );
    }
  };

  const confirmMissingDatesDataDelete = () => {
    setConfirmationDialogAttributes({
      title: "Missing dates delete confirmation!",
      message: "Are you sure to remove saved reasons for selected date(s)?",
      confirmText: "Yes",
      cancelText: "No",
      url: "Roles/",
      keyField: "roleKey",
      key: "missingDates",
      status: "",
      messageTitle: "Missing dates",
    });
    setDialogOpen(true);
  };

  const resetData = () => {
    setTimeout(() => {
      setSelectedRows([]);
      updateMissingDataReasonsDetailsValue(initialMissingDataReasonsDetails);
    }, 2000);
  };

  useEffect(() => {}, [missingDataReasonsDetails]);

  return (
    <div
      style={{ display: "flex", gap: "20px", height: "calc(100vh - 160px)" }}
    >
      <Backdrop
        className="backDrop"
        open={
          isLoadingMissingReasons
            ? true
            : isLoadingMissingReasonsDates
            ? true
            : isLoadingMissingDataSave
            ? true
            : isLoadingMissingDataDelete
            ? true
            : false
        }
      >
        <CircularProgress color="inherit" />
        <p>Please wait...</p>
      </Backdrop>
      <ConfirmationDialog
        open={dialogOpen}
        setDialogOpen={setDialogOpen}
        confirmationDialogAttributes={confirmationDialogAttributes}
        deleteMissingDatesData={() => {
          deleteMissingDatesData();
        }}
      />
      <div
        style={{
          display: "flex",
          flexFlow: "column",
          width: "100%",
        }}
      >
        <MessageDisplay />
        {facilityId > 0 && (
          <>
            <Grid
              container
              sx={{ display: "flex", gap: "15px", paddingTop: "15px" }}
            >
              <Grid item xs={2} sx={{ display: "flex" }}>
                <div style={{ display: "-webkit-box" }}>
                  <b>Start Date:</b> &nbsp;
                  <div>
                    <InputDate
                      label=""
                      id={"missingDataReasonsDetails.startDate"}
                      value={missingDataReasonsDetails.startDate}
                      onChange={(e) => onDateChangeHandler(e, "startDate")}
                      isRequired={false}
                      isError={missingDataReasonsDetails?.startDate === ""}
                      displayType={"inline"}
                    />
                  </div>
                </div>
              </Grid>
              <Grid item xs={1} sx={{ display: "flex" }}></Grid>
              <Grid item xs={2} sx={{ display: "flex" }}>
                <div style={{ display: "-webkit-box" }}>
                  <b>End Date:</b> &nbsp;
                  <div>
                    <InputDate
                      label=""
                      id={"missingDataReasonsDetails.endDate"}
                      value={missingDataReasonsDetails.endDate}
                      onChange={(e) => onDateChangeHandler(e, "endDate")}
                      isRequired={false}
                      isError={missingDataReasonsDetails?.endDate === ""}
                      displayType={"inline"}
                    />
                  </div>
                </div>
              </Grid>
              <Grid item xs={1} sx={{ display: "flex" }}></Grid>
              <Grid item xs={2} sx={{ display: "flex", gap: "20px" }}>
                <AddNew
                  onClickCall={() => {
                    getMissingReasonsByDates();
                  }}
                  title={"Load"}
                />
                <AddNew
                  onClickCall={() => {
                    resetData();
                  }}
                  title={"Reset"}
                />
              </Grid>
            </Grid>
            <br />
            <br />
            {missingDataReasonsDetails?.isLoadButtonClicked && (
              <>
                <Grid container sx={{ display: "flex", gap: "0px" }}>
                  <Grid
                    item
                    lg={6}
                    md={6}
                    xs={6}
                    sx={{
                      display: "block",
                      padding: "20px",
                      border: "1px solid gray",
                    }}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      xs={12}
                      sx={{ padding: "0px 0px 20px 0px" }}
                    >
                      {missingReasonsDates?.length > 0 && (
                        <>
                          <input
                            id="checkAll"
                            type="checkbox"
                            sx={{ verticalAlign: "text-bottom", color: "blue" }}
                            checked={missingDataReasonsDetails.checkAll}
                            onChange={(e) => onChangeHandler(e, "checkAll")}
                          />
                          &nbsp; Select All
                        </>
                      )}
                    </Grid>
                    <Grid
                      item
                      lg={12}
                      md={12}
                      xs={12}
                      sx={{ padding: "0px 0px 20px 0px" }}
                    >
                      {missingReasonsDates?.length > 0 && (
                        <>
                          <TableForMissingDates
                            pagging={false}
                            columns={missingReasonsDateColumns}
                            data={missingReasonsDates}
                            selectedRows={selectedRows}
                            setSelectedRows={setSelectedRows}
                          />
                        </>
                      )}
                      {missingReasonsDates?.length <= 0 && (
                        <>Missing dates not found for the given date range...</>
                      )}
                    </Grid>
                  </Grid>

                  <Grid
                    item
                    lg={6}
                    md={6}
                    xs={6}
                    sx={{
                      display: "block",
                      padding: "20px",
                      border: "1px solid gray",
                    }}
                  >
                    <Grid item lg={12} md={12} xs={12}>
                      <input
                        id="checkDelete"
                        type="checkbox"
                        sx={{ verticalAlign: "text-bottom" }}
                        checked={missingDataReasonsDetails.checkDelete}
                        onChange={(e) => onChangeHandler(e, "checkDelete")}
                      />
                      &nbsp; Remove saved reasons
                    </Grid>
                    <br />
                    {!missingDataReasonsDetails?.checkDelete && (
                      <>
                        <Grid item lg={12} md={12} xs={12}>
                          <ReportsSelectCheckboxs
                            isMulti={false}
                            showInLeft={true}
                            title="Reasons"
                            id="ddlReasons"
                            list={missingReasons}
                            changeHandler={indexChangeHandler}
                            startUnitDisplay={false}
                            selectedReportId={"101"}
                          />
                        </Grid>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          xs={12}
                          sx={{ display: "flex", padding: "20px 0px 20px 0px" }}
                        >
                          <Grid item lg={2} md={2} sm={2}></Grid>
                          <Grid item lg={10} md={10} sm={10}>
                            <input
                              type="text"
                              id="otherReason"
                              disabled={checkOtherReasonDisabled()}
                              onChange={(e) =>
                                onChangeHandler(e, "otherReason")
                              }
                              style={{
                                height: "36px",
                                maxLength: "500",
                                width: "59%",
                              }}
                              placeholder="--enter details for other reason..."
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>
                <Grid
                  container
                  sx={{
                    display: "flex",
                    gap: "20px",
                    justifyContent: "center",
                    paddingTop: "20px",
                  }}
                >
                  {missingReasonsDates?.length > 0 && (
                    <AddNew
                      onClickCall={() => {
                        validateMissingData();
                      }}
                      title={"Update"}
                    />
                  )}
                </Grid>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
